import React, { useMemo } from 'react'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { useNavigate, useParams } from 'react-router-dom'
import { DriveViewContext, DriveViewAlbum } from '@/contexts/driveViewContext.tsx'
import { DriveViewFilesContext } from '@/contexts/driveViewFilesContext.tsx'
import { AlbumFile } from '@/models/albumFile.ts'
import { getURLParamsForFilter, useFileFilter } from '@/contexts/fileFilter.tsx'
import { resolveTheme } from '@/models/drive.ts'

export function PortfolioDriveViewProvider({ children }: { children: React.ReactNode }) {
  const { getPortfolioProject, getPortfolioAlbums, getPortfolioAlbumFiles } = useClient()
  const { portfolioId, projectId, albumId } = useParams<{ portfolioId: string, projectId: string, albumId?: string }>()
  const navigate = useNavigate()

  const projectQuery = useQuery({
    queryKey: ['portfolio', portfolioId, 'project', projectId],
    queryFn: async () => {
      if (!portfolioId || !projectId) return null
      const res = await getPortfolioProject(portfolioId, projectId)
      return res.data
    },
    enabled: !!portfolioId && !!projectId,
  })

  const albumsQuery = useQuery({
    queryKey: ['portfolio', portfolioId, 'project', projectId, 'albums'],
    queryFn: async () => {
      if (!portfolioId || !projectId) return []
      const res = await getPortfolioAlbums(portfolioId, projectId)
      return res.data
    },
    enabled: !!portfolioId && !!projectId,
  })

  const albums: DriveViewAlbum[] = useMemo(() => {
    return (albumsQuery.data || []).map(a => ({
      id: a.id,
      album_id: a.album_id,
      title: a.title,
      description: a.description,
      thumbnail: a.thumbnail,
      files_count: a.files_count,
      preview_desktop: a.preview_desktop,
      preview_mobile: a.preview_mobile,
      preview_desktop_center_x: a.preview_desktop_center_x,
      preview_desktop_center_y: a.preview_desktop_center_y,
      preview_mobile_center_x: a.preview_mobile_center_x,
      preview_mobile_center_y: a.preview_mobile_center_y,
    }))
  }, [albumsQuery.data])

  const currentAlbum = useMemo(() => {
    if (!albums.length) return null
    if (albumId) {
      return albums.find(a => a.id === albumId) || albums[0]
    }
    return albums[0]
  }, [albums, albumId])

  function onSelectAlbum(album: DriveViewAlbum) {
    navigate(`/p/${portfolioId}/${projectId}/${album.id}`)
  }

  async function fetchAlbumFiles(albumId: string) {
    const res = await getPortfolioAlbumFiles(portfolioId!, projectId!, albumId)
    return res.data
  }

  const metadata = useMemo(() => {
    if (!projectQuery.data) return null
    return {
      title: projectQuery.data.title,
      driveTitle: projectQuery.data.drive_title,
      driveType: projectQuery.data.drive_type,
      theme: resolveTheme(projectQuery.data.theme_name, projectQuery.data.drive_type),
      spacing: projectQuery.data.spacing,
      layoutMode: projectQuery.data.layout_mode,
      albumOverview: projectQuery.data.album_overview,
      albumOverviewStyle: projectQuery.data.album_overview_style,
      areElementsContained: projectQuery.data.are_elements_contained,
      contact: projectQuery.data.contact,
      userDefinedExpiresAt: projectQuery.data.user_defined_expires_at,
      openedImageCaption: projectQuery.data.opened_image_caption,
      downloadOptions: projectQuery.data.download_options,
    }
  }, [projectQuery.data])

  return (
    <DriveViewContext.Provider value={{
      metadata,
      albums,
      currentAlbum,
      isLoading: projectQuery.isLoading || albumsQuery.isLoading,
      error: (projectQuery.error || albumsQuery.error) as Error | null,
      onSelectAlbum,
      fetchAlbumFiles,
    }}>
      {currentAlbum ? (
        <PortfolioDriveViewFilesProvider portfolioId={portfolioId!} projectId={projectId!} albumId={currentAlbum.id}>
          {children}
        </PortfolioDriveViewFilesProvider>
      ) : (
        <DriveViewFilesContext.Provider value={null}>
          {children}
        </DriveViewFilesContext.Provider>
      )}
    </DriveViewContext.Provider>
  )
}

function PortfolioDriveViewFilesProvider({ portfolioId, projectId, albumId, children }: { portfolioId: string, projectId: string, albumId: string, children: React.ReactNode }) {
  const { getPortfolioAlbumFiles } = useClient()
  const { fileFilter } = useFileFilter()

  const filterParams = useMemo(() => {
    if (!fileFilter) return undefined
    const params = getURLParamsForFilter(fileFilter)
    if (params.length === 0) return undefined
    return Object.fromEntries(params)
  }, [fileFilter])

  const query = useInfiniteQuery({
    enabled: !!portfolioId && !!projectId && !!albumId,
    queryKey: ['portfolio', portfolioId, 'project', projectId, 'album', albumId, 'files', filterParams],
    initialPageParam: null as string | null,
    async queryFn({ pageParam }) {
      if (pageParam) {
        const response = await fetch(pageParam)
        return response.json()
      }
      const res = await getPortfolioAlbumFiles(portfolioId, projectId, albumId, filterParams)
      return res.data
    },
    getNextPageParam(page: any) {
      return page.next
    },
  })

  const fetchedFiles = useMemo(() => {
    if (!query.data) return []
    const files: Array<AlbumFile> = [
      // @ts-ignore
      ...(query.data.pages.flatMap((page: any) => page.results || page))
    ]
    return files
  }, [query.data])

  return (
    <DriveViewFilesContext.Provider value={{
      currentFilesCollection: { files: fetchedFiles },
      hasNextPage: query.hasNextPage,
      fetchNextPage: query.fetchNextPage,
      isLoading: query.isLoading,
    }}>
      {children}
    </DriveViewFilesContext.Provider>
  )
}
